import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useThree, Canvas, useFrame } from 'react-three-fiber';
import MersenneTwist from 'mersenne-twister';
import { TorusKnot, useAspect } from '@react-three/drei';
import Color from 'color';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader';
// import { ConvolutionShader } from 'three/examples/jsm/shaders/ConvolutionShader';

import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { TexturePass } from 'three/examples/jsm/postprocessing/TexturePass';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass';
import { Path } from 'three';
import { Helmet } from "react-helmet";

// import recorder from 'react-canvas-recorder';
import ReactPlayer from 'react-player';
import { set } from 'core-js/core/dict';

// const glslify = require('glslify');


const physics_vertex = `
  precision highp float;
  
  attribute vec3 position;
  void main() {
    gl_Position = vec4(position, 1.);
  }
`;

const physics_frag = `
  #define PI 3.1415926535897932384626433832795

  precision highp float;
  
  uniform sampler2D physicsData;
  uniform sampler2D trail;
  uniform sampler2D backTrail;
  uniform vec2 bounds;
  uniform vec2 screenBounds;
  uniform float time;
  uniform int useRandom;
  uniform float speedDecrease;
  uniform int rebound;

  uniform float sensorAngle;
  uniform float particleAngle;
  uniform float sensorLength;

  const int POSITION_SLOT = 0;
  const int VELOCITY_SLOT = 1;
  // const int COLOR_SLOT = 2;

  vec3 rgb2hsv(vec3 c) {
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

    float d = q.x - min(q.w, q.y);
    float e = 1.0e-10;
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
  }

  vec3 hsv2rgb(vec3 c){
    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
  }
  
  vec4 texel(vec2 origin, vec2 offset, vec2 bounds, sampler2D txt) {
    vec2 coord = (origin + offset) / bounds;
    return texture2D(txt, coord);
  }
  
  // float random (vec2 st) {
  //   return fract(sin(dot(st.xy,
  //                        vec2(12.9898,78.233)))*
  //       43758.5453123);
  // }
  
  float hash(float p) {
    p = fract(p * .1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }

  float hash(vec2 p) {
    vec3 p3  = fract(vec3(p.xyx) * .1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    int slot = int(mod(gl_FragCoord.x, 2.0));
    if (slot == POSITION_SLOT) {
      vec4 dataA = texel(gl_FragCoord.xy, vec2(0, 0), bounds, physicsData);
      vec4 dataB = texel(gl_FragCoord.xy, vec2(1, 0), bounds, physicsData);
      vec3 position = dataA.xyz;
      float phi = dataA.w;
      vec3 velocity = dataB.xyz;
      float delay = dataB.w;

      if (time <= delay) {
        gl_FragColor = vec4(position, phi);
      } else {

        vec2 sensor1 = vec2(
          cos(phi - sensorAngle) * sensorLength,
          sin(phi - sensorAngle) * sensorLength
        );
        vec4 tex1 = texel(position.xy, sensor1, screenBounds, trail);
        vec4 btex1 = texel(position.xy, sensor1, screenBounds, backTrail);
        // btex1 = vec4(10.0, 10.0, 10.0, 1.0);
        tex1 += btex1;
        vec3 hsv1 = rgb2hsv(tex1.rgb);
        // vec3 hsv1 = vec3(1.0);
  
        vec2 sensor2 = vec2(
          cos(phi) * sensorLength,
          sin(phi) * sensorLength
        );
        vec4 tex2 = texel(position.xy, sensor2, screenBounds, trail);
        vec4 btex2 = texel(position.xy, sensor2, screenBounds, backTrail);
        // btex2 = vec4(10.0, 10.0, 10.0, 1.0);
        tex2 += btex2;
        vec3 hsv2 = rgb2hsv(tex2.rgb);
        
        vec2 sensor3 = vec2(
          cos(phi + sensorAngle) * sensorLength,
          sin(phi + sensorAngle) * sensorLength
        );
        vec4 tex3 = texel(position.xy, sensor3, screenBounds, trail);
        vec4 btex3 = texel(position.xy, sensor3, screenBounds, backTrail);
        // btex3 = vec4(10.0, 10.0, 10.0, 1.0);
        tex3 += btex3;
        vec3 hsv3 = rgb2hsv(tex3.rgb);
        // vec3 hsv3 = vec3(1.0);
  
        if (
          hsv1.z < 0.1
        ) {
          // velocity.xy = vec2(0.);
        }
        else if (
          hsv2.z < 0.1
        ) {
          phi -= particleAngle;
          if (phi < 0.0) {
            phi += 2. * PI;
          }
        }
        else if (
          hsv3.z < 0.1
        ) {
          phi += particleAngle;
          if (phi > 2. * PI) {
            phi -= 2. * PI;
          }
        }
        else {
          // TODO random
          if (useRandom == 1) {
            phi = hash(position.xy) * 2. * PI;
          }
          else if (useRandom == 2) {
            phi = phi + (hash(position.xy) - 0.5) * 0.2 * 2. * PI;
            velocity = -velocity;
          }
        }
        
        vec3 oldPos = vec3(position);
        position.x += cos(phi) * velocity.x;
        position.y += sin(phi) * velocity.y;

        if (rebound > 0) {
          if (position.x <= 0.) {
            if (phi < PI) {
              phi = PI - phi;
            } else {
              phi = 3. * PI - phi;
            }
            position.x = oldPos.x + cos(phi) * velocity.x;
            position.y = oldPos.y + sin(phi) * velocity.y;
          }
          else if (position.y >= screenBounds.y) {
            phi = 2.* PI - phi;
            position.x = oldPos.x + cos(phi) * velocity.x;
            position.y = oldPos.y + sin(phi) * velocity.y;
          }
          else if (position.x >= screenBounds.x) {
            phi = PI - phi;
            position.x = oldPos.x + cos(phi) * velocity.x;
            position.y = oldPos.y + sin(phi) * velocity.y;
          }
          else if (position.y <= 0.) {
            if (phi < PI) {
              phi = PI - phi;
            } else {
              phi = 2. * PI - phi;
            }
            position.x = oldPos.x + cos(phi) * velocity.x;
            position.y = oldPos.y + sin(phi) * velocity.y;
          }
        }

        gl_FragColor = vec4(position, phi);
      }
    }
    else {
      // vec4 dataA = texel(gl_FragCoord.xy, vec2(-1, 0), bounds, physicsData);
      vec4 dataB = texel(gl_FragCoord.xy, vec2(0, 0), bounds, physicsData);
      // vec4 dataC = texel(gl_FragCoord.xy, vec2(1, 0), bounds, physicsData);
      // vec3 position = dataA.xyz;
      vec3 velocity = dataB.xyz;

      velocity *= speedDecrease;

      gl_FragColor = vec4(velocity, dataB.w); //vec4(velocity, 1.0);
    }

  }
`;

const render_vert = `
  precision highp float;
  
  uniform mat4 modelViewMatrix;
  uniform mat4 projectionMatrix;
  uniform vec2 bounds;
  uniform vec2 colorBounds;
  uniform vec2 screenBounds;
  uniform sampler2D physicsData;
  uniform sampler2D colorData;
  uniform sampler2D trail;
  uniform float particleSize;
  uniform float step;

  attribute vec3 position;
  attribute vec2 uv;
  uniform float time;

  varying vec2 vUv;
  varying vec4 c;

    
  vec4 texel(vec2 origin, vec2 offset, vec2 bounds, sampler2D txt) {
    vec2 coord = (origin + offset) / bounds;
    return texture2D(txt, coord);
  }

  void main() {
    vUv = uv;
    vec4 particle = texel(position.xy, vec2(0., 0.), bounds, physicsData);
    vec4 velocity = texel(position.xy, vec2(1., 0.), bounds, physicsData);
    float delay = velocity.w;

    if (time <= delay) {
      c = texel(position.xy, vec2(0., 0.), screenBounds, trail);
    } else {
      c = texel(position.xy, vec2(0., 0.), bounds, colorData);
    }
    // c = texel(position.xy, vec2(0., 0.), bounds, colorData);
    vec4 pos = vec4(particle.xyz, 1.0);
    pos.xy -= screenBounds / 2.;
    
    vec4 mvPosition = modelViewMatrix * pos;
    vec4 finalPosition = projectionMatrix * mvPosition;
    
    gl_Position = finalPosition;
    gl_PointSize = particleSize;
  }
`;

const render_frag = `
  #define PI 3.1415926535897932384626433832795

  precision highp float;
  
  //uniform sampler2D particleTexture;

  varying vec2 vUv;
  varying vec4 c;

  float rgbToFloat(vec3 rgb, float scale){
    return rgb.r +
        (rgb.g/scale)+
        (rgb.b/(scale*scale));
  }

  // vec3 floatToRgb(float v, float scale) {
  //     float r = v;
  //     float g = mod(v*scale, scale);
  //     r -= g/scale;
  //     float b = mod(v*scale*scale, scale);
  //     g -= b/scale;
  //     return vec3(r, g, b);
  // }
  
  // vec3 EncodeFloatRGBA(float v) {
  //   vec2 enc = vec2(1.0, 255.0) * v;
  //   float r = floor(enc.x);
  //   float f = fract(enc.x);
  //   float g = f * 255.;
  //   f = fract(enc.y);
  //   float b = f * 255.;

  //   return vec3(r, g, b);
  // }

  // float DecodeFloatRGBA(vec3 rgb) {
  //   return dot(rgb, vec3(1.0, 1. / 255.0, 1. / 65025.0) );
  // }

  void main() {
    // vec4 trail_texel = texture2D(trail, vUv);
    // vec3 rgb = floatToRgb(c, 255.);
    // vec3 rgb = EncodeFloatRGBA(c);

    gl_FragColor = c; //texture2D(particleTexture, gl_PointCoord);
  }
`;



const DiffuseShader = {

  uniforms: {
    'tDiffuse': { value: null },
    'bounds': { value: null },
    'trail': { value: null },
    'ratio': { value: null },
    'trailRatio': { value: null },
    'time': { value: 0.0 },
    'trailSmooth': { value: 0.0 }
  },

  vertexShader: /* glsl */`
    precision highp float;
    
    uniform mat4 modelViewMatrix;
    uniform mat4 projectionMatrix;

    attribute vec3 position;
    attribute vec2 uv;

    varying vec2 vUv;
    
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    } `,

  fragmentShader: /* glsl */`
    precision highp float;
    
		uniform sampler2D tDiffuse;
		uniform sampler2D trail;
		uniform vec2 bounds;

		uniform float ratio;
		uniform float trailRatio;
    uniform float time;
		uniform float trailSmooth;

		varying vec2 vUv;

    vec3 rgb2hsv(vec3 c)
    {
        vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
        vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
        vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

        float d = q.x - min(q.w, q.y);
        float e = 1.0e-10;
        return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
    }

    vec3 hsv2rgb(vec3 c)
    {
        vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
    }

    float hueDistance(vec3 hsv1, vec3 hsv2) {
      return min(abs(hsv2.x - hsv1.x), 1.0 - abs(hsv2.x - hsv1.x));
    }
        
    void main() {
      float dx = 1. / bounds.x;
      float dy = 1. / bounds.y;
      vec4 t = texture2D(tDiffuse, vUv);
      vec4 tTrail = texture2D(trail, vUv);

      vec3 red = rgb2hsv(vec3(1.0, 0., 0.));
      vec3 tHsv = rgb2hsv(t.rgb);
      vec3 tTrailHsv = rgb2hsv(tTrail.rgb);

      vec4 t1 = texture2D(tDiffuse, vUv + vec2(dx, 0.));
      vec4 t2 = texture2D(tDiffuse, vUv + vec2(dx, dy));
      vec4 t3 = texture2D(tDiffuse, vUv + vec2(0., dy));
      vec4 t4 = texture2D(tDiffuse, vUv + vec2(-dx, dy));
      vec4 t5 = texture2D(tDiffuse, vUv + vec2(-dx, 0.));
      vec4 t6 = texture2D(tDiffuse, vUv + vec2(-dx, -dy));
      vec4 t7 = texture2D(tDiffuse, vUv + vec2(0., -dy));
      vec4 t8 = texture2D(tDiffuse, vUv + vec2(dx, -dy));
      // float tr = trailRatio;
      // if (tTrailHsv.x < 0.01 && tTrailHsv.y < 0.01 && tTrailHsv.z > 0.9) {
      //   tTrail.rgb *= 0.8;
      // }
      // t.rgb *= 3. * t.rgb;
      t.rgb += 
        t1.rgb +
        t2.rgb +
        t3.rgb +
        t4.rgb +
        t5.rgb +
        t6.rgb +
        t7.rgb +
        t8.rgb;

      t.rgb /= 9.;

      if (trailSmooth > 0.0) {
        vec4 tt1 = texture2D(trail, vUv + vec2(dx, 0.));
        vec4 tt2 = texture2D(trail, vUv + vec2(dx, dy));
        vec4 tt3 = texture2D(trail, vUv + vec2(0., dy));
        vec4 tt4 = texture2D(trail, vUv + vec2(-dx, dy));
        vec4 tt5 = texture2D(trail, vUv + vec2(-dx, 0.));
        vec4 tt6 = texture2D(trail, vUv + vec2(-dx, -dy));
        vec4 tt7 = texture2D(trail, vUv + vec2(0., -dy));
        vec4 tt8 = texture2D(trail, vUv + vec2(dx, -dy));
  
        tTrail.rgb = trailSmooth * tTrail.rgb;
        tTrail.rgb += 
          tt1.rgb +
          // tt2.rgb +
          tt3.rgb +
          // tt4.rgb +
          tt5.rgb +
          // tt6.rgb +
          tt7.rgb;
          // tt8.rgb;
      
        tTrail.rgb /= 4. + trailSmooth;
      }

      t.rgb = t.rgb * ratio  + tTrail.rgb * trailRatio;
      t.a = 1.0;

      t.rgb = clamp(t.rgb, 0., 255.);

      gl_FragColor = t;
    }
  `

};


const DecayShader = {

  uniforms: {

    'tDiffuse': { value: null },
    'decay': { value: null }

  },

  vertexShader: /* glsl */`
    precision highp float;
    
    uniform mat4 modelViewMatrix;
    uniform mat4 projectionMatrix;

    attribute vec3 position;
    attribute vec2 uv;

    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    } `,

  fragmentShader: /* glsl */`
    precision highp float;
    
    uniform sampler2D tDiffuse;
    varying vec2 vUv;
    uniform float decay;
    uniform float time;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      texel.rgb -= decay;
      texel.rgb = clamp(texel.rgb, 0., 255.);
      gl_FragColor = texel;
    }
  `

};


const ResizeShader = {

  uniforms: {

    'tDiffuse': { value: null },
    'bounds': { value: null },
    'screenBounds': { value: null },
    'colorMultiplier': { value: 1.0 },
    'paletteType': { value: 0.0 },
    'paletteA': { value: null },
    'paletteB': { value: null },
    'paletteC': { value: null },
    'paletteD': { value: null }
  },

  vertexShader: /* glsl */`
    precision highp float;
    
    uniform mat4 modelViewMatrix;
    uniform mat4 projectionMatrix;
    uniform vec2 bounds;
    uniform vec2 screenBounds;
    
    attribute vec3 position;
    attribute vec2 uv;

    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      // gl_Position = vec4(position.xy, 0., 1.);
    }
  `,

  fragmentShader: /* glsl */`
    precision highp float;
    
    uniform sampler2D tDiffuse;
    uniform vec2 bounds;
    uniform vec2 screenBounds;
    uniform float colorMultiplier;
    uniform int paletteType;
    uniform vec3 paletteA;
    uniform vec3 paletteB;
    uniform vec3 paletteC;
    uniform vec3 paletteD;

    varying vec2 vUv;

    vec3 palette(float t, vec3 a, vec3 b, vec3 c, vec3 d) {
      return a + b * cos(6.28318 * (c * t + d));
    }

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv); //vec2(uvx, uvy));
      if (paletteType > 0) {
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 1.0, 0.5), vec3(0.80, 0.90, 0.30));
        
        // a		b	c	d	palette
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 1.0, 1.0), vec3(0.00, 0.33, 0.67));
        // vec3 p = palette(texel.x, vec3(0.0, 0.25, 0.25), vec3(0.3, 0.4, 0.5), vec3(0.7, 1.0, 1.0), vec3(0.0, 0.4, 0.6));
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 1.0, 1.0), vec3(0.00, 0.10, 0.20));
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 0.75, 0.5), vec3(0.00, 0.10, 0.20));
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 1.0, 1.0), vec3(0.30, 0.20, 0.20));
        
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 1.0, 0.5), vec3(0.80, 0.90, 0.30));
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 0.7, 0.4), vec3(0.00, 0.15, 0.20));
        // vec3 p = palette(texel.x, vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.5), vec3(1.0, 1.0, 0.0), vec3(0.50, 0.20, 0.25));
        // vec3 p = palette(texel.x, vec3(0.8, 0.5, 0.4), vec3(0.2, 0.4, 0.2), vec3(2.0, 1.0, 1.0), vec3(0.00, 0.25, 0.25));
        // vec3 p = palette(texel.x, vec3(0.0,0.0,0.0),vec3(0.5,0.5,0.5),vec3(2.0,0.7,0.5),vec3(0.5,0.5,0.5));
        
        // vec3 p = palette(texel.x,
        //   vec3(0.5, 0.5, 0.5), vec3(0.5, 0.5, 0.3),
        //   vec3(1.0, 1.0, 1.0), vec3(0.05, 0.0, 0.0)
        // );
        vec3 p = palette(texel.x, paletteA, paletteB, paletteC, paletteD);
        texel.xyz = p.xyz;
      }
      texel *= colorMultiplier;
      // uv * bounds / screenBounds + (screenBounds - bounds) / (2. * screenBounds));
      // (uv + (screenBounds / bounds - 1.) / 2.) * bounds / screenBounds); // 
      gl_FragColor = texel;
    }
  `

};

const SEED = 42;
// const COLLECTION = "shape";
// const TOKEN_ID = 1;

const STATIC_CONFIG = {
  "SEED": 42,
  "COLLECTION": "shape",
  "TOKEN_ID": 1,
  "NAME": "Infect The Grid - Shape 131K/2K #1",
  "PARTICLE_COUNT": 131072,
  "PARTICLE_SIZE": 1.0,
  "WIDTH": 1920,
  "HEIGHT": 1080,
  "SENSOR_LENGTH": 5,
  "SENSOR_ANGLE": 0.78539816339,
  "PARTICLE_ANGLE": 0.1,
  "DIFFUSE_RATIO": 0.005,
  "DIFFUSE_TRAIL_RATIO": 0.99,
  "DIFFUSE_TRAIL_SMOOTH": 25,
  "DECAY": 0.005,
  "SPEED": 9.0,
  "COLOR": [
    232,
    72,
    114
  ],
  "RADIUS_RATIO_X": 0.25,
  "RADIUS_RATIO_Y": 0.25,
  "GROUP_NB": 1200,
  "GRID_NB": 24,
  "COLOR_MODE": "none",
  "COLOR_MULTIPLIER": 0.1,
  "FINAL_COLOR_MULTIPLIER": 5.0,
  "TIME_MODE": "i",
  "SHAPE_MODE": "grid",
  "ANGLE_MODE": "grid",
  "TIME_GROUP_INTERVAL": 0.0001,
  "TIME_GROUP_START": 1.0,
  "GROUP_DIST": 250,
  "SPEED_DECREASE": 1.0,
  "REBOUND": 1,
  "INIT_TRAIL": "none",
  "PARTICLE_RANDOM": 2,
  "PALETTE_TYPE": 0
}

const PARTICLE_DATA_SLOTS = 2;
// const PARTICLE_COUNT = 128 * 128; // 1024 * 1024; //Math.pow(1024, 2);
// const PARTICLE_COUNT_SQRT = Math.sqrt(PARTICLE_COUNT);
// const PARTICLE_DATA_WIDTH = PARTICLE_COUNT_SQRT * PARTICLE_DATA_SLOTS;
// const PARTICLE_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

// const WIDTH = 1920; //3840; //1920;
// const HEIGHT = 1080; //2160; //1080;
// const SENSOR_LENGTH = 66.0;
// const SENSOR_ANGLE = Math.PI / 4;
// const PARTICLE_ANGLE = Math.PI / 4;
// const DIFFUSE_RATIO = 0.2;
// const DIFFUSE_TRAIL_RATIO = 1.001;
// const PARTICLE_SIZE = 2.0;
// const DECAY = 0.004;
// const SPEED = 1.0;

const randomizer = new MersenneTwist(SEED);

function rgbToFloat(rgb, scale) {
  return rgb[0] +
    (rgb[1] / scale) +
    (rgb[2] / (scale * scale));
}

const bitDec = [1.0, 1 / 255.0, 1 / 65025.0];
function DecodeFloatRGBA(rgb) {
  return rgb[0] * bitDec[0] + rgb[1] * bitDec[1] + rgb[2] * bitDec[2];
}

const createPhysicsDataTexture = (width, height, config) => {
  if (!config) {
    return null;
  }
  const PARTICLE_COUNT = config.PARTICLE_COUNT;
  const SPEED = config.SPEED;
  const PARTICLE_COUNT_SQRT = Math.sqrt(PARTICLE_COUNT);
  const PARTICLE_DATA_WIDTH = PARTICLE_COUNT_SQRT * PARTICLE_DATA_SLOTS;
  const PARTICLE_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

  const COLOR_DATA_WIDTH = PARTICLE_COUNT_SQRT;
  const COLOR_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

  const size = 4 * PARTICLE_COUNT * PARTICLE_DATA_SLOTS;
  const colorSize = 4 * PARTICLE_COUNT;

  const data = new Float32Array(size);
  const colorData = new Float32Array(colorSize);
  const lg = 4 * PARTICLE_DATA_SLOTS;
  const colorLg = 4;

  const RADIUS_RATIO_X = config.RADIUS_RATIO_X;
  const RADIUS_RATIO_Y = config.RADIUS_RATIO_Y;
  const GROUP_NB = config.GROUP_NB;
  let GROUP_DIVIDER;
  if ("GROUP_DIVIDER" in config) {
    GROUP_DIVIDER = config.GROUP_DIVIDER;
  } else {
    GROUP_DIVIDER = GROUP_NB;
  }

  let TIME_GROUP_START = 0.0;
  if ("TIME_GROUP_START" in config) {
    TIME_GROUP_START = config.TIME_GROUP_START;
  }


  let COLOR_MULTIPLIER = 1.0;
  if ("COLOR_MULTIPLIER" in config) {
    COLOR_MULTIPLIER = config.COLOR_MULTIPLIER;
  }

  const COLOR_MODE = config.COLOR_MODE;
  const TIME_MODE = config.TIME_MODE;
  const ANGLE_MODE = config.ANGLE_MODE;
  const SHAPE_MODE = config.SHAPE_MODE;

  const scale = window.devicePixelRatio;
  const D = Math.min(width, height);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    // let idx = randomizer.random() * width * height;
    let stride = i * lg;
    let colorStride = i * colorLg;

    let a, x, y, grp, gridGrp;
    let GRID_NB = config.GRID_NB;

    grp = i % GROUP_NB;
    a = grp / GROUP_DIVIDER;
    gridGrp = grp;

    if (SHAPE_MODE === "grid") {
      gridGrp = i % GRID_NB;

      let relGridNb = GRID_NB / (4.0 * 2.0);
      let relGrid = Math.floor(gridGrp / 4.0) - (GRID_NB / 4.0 - 1.0) / 2.0;
      if (gridGrp % 4 === 0) {
        x = (randomizer.random() - 0.5) * 3. +
          (width / 2. + relGrid * (RADIUS_RATIO_X * D) / relGridNb);
        y = height;
      }
      else if (gridGrp % 4 === 1) {
        x = (randomizer.random() - 0.5) * 3. +
          (width / 2. + relGrid * (RADIUS_RATIO_X * D) / relGridNb);
        y = 0.0;
      }
      else if (gridGrp % 4 === 2) {
        x = 0.0;
        y = (randomizer.random() - 0.5) * 3. +
          (height / 2. + relGrid * (RADIUS_RATIO_Y * D) / relGridNb);
      }

      else if (gridGrp % 4 === 3) {
        x = width;
        y = (randomizer.random() - 0.5) * 3. +
          (height / 2. + relGrid * (RADIUS_RATIO_Y * D) / relGridNb);
      }

    }
    else if (SHAPE_MODE === "rect") {
      a = (i % GROUP_NB) * 2 * Math.PI / GROUP_DIVIDER;
      const PI4 = Math.PI / 4.;
      const D = Math.min(width, height);
      if (a > PI4 && a <= 3 * PI4) {
        x = width / 2. + D * RADIUS_RATIO_Y / Math.tan(a);
        y = height / 2. + RADIUS_RATIO_Y * D;
      }
      else if (a > 3 * PI4 && a <= 5 * PI4) {
        x = width / 2. - RADIUS_RATIO_X * D;
        y = height / 2. - Math.tan(a) * D * RADIUS_RATIO_X;
      }
      else if (a > 5 * PI4 && a <= 7 * PI4) {
        x = width / 2. - D * RADIUS_RATIO_Y / Math.tan(a);
        y = height / 2. - RADIUS_RATIO_Y * D;
      }
      else {
        x = width / 2. + RADIUS_RATIO_X * D;
        y = height / 2. + Math.tan(a) * RADIUS_RATIO_X * D;
      }

    }
    else if (SHAPE_MODE === "canon") {
      grp = i % GROUP_NB;
      a = grp / GROUP_DIVIDER;
      x = 200.;
      y = height / 2.;
    }
    else if (SHAPE_MODE === "disc") {
      grp = i % GROUP_NB;
      a = grp / GROUP_NB;
      x = Math.cos(a * (2 * Math.PI)) * D * RADIUS_RATIO_X + width / 2.;
      y = Math.sin(a * (2 * Math.PI)) * D * RADIUS_RATIO_Y + height / 2.;
    }
    else if (SHAPE_MODE === "circle2") {
      grp = i % GROUP_NB;
      a = grp / GROUP_DIVIDER;

      x = Math.cos(i * (2 * Math.PI) / PARTICLE_COUNT) * (D + grp * config.GROUP_DIST) * RADIUS_RATIO_X + width / 2.;
      y = Math.sin(i * (2 * Math.PI) / PARTICLE_COUNT) * (D + grp * config.GROUP_DIST) * RADIUS_RATIO_Y + height / 2.;
    }
    else {
      grp = (i % GROUP_NB);
      a = grp / GROUP_DIVIDER;
      x = Math.cos(a * 2 * Math.PI) * D * RADIUS_RATIO_X + width / 2.;
      y = Math.sin(a * 2 * Math.PI) * D * RADIUS_RATIO_Y + height / 2.;
    }

    // position
    data[stride] = x; //idx % width;
    data[stride + 1] = y; //idx / width;
    let rgb;
    if (COLOR_MODE === "hsv") {
      rgb = HSVtoRGB(a, 1.0, 1.0);
    }
    else if (COLOR_MODE === "group") {
      rgb = HSVtoRGB(a, 1.0, 1.0);
      rgb = { r: rgb.r * COLOR_MULTIPLIER, g: rgb.g * COLOR_MULTIPLIER, b: rgb.b * COLOR_MULTIPLIER };
    }
    else {
      rgb = { r: config.COLOR[0] * COLOR_MULTIPLIER, g: config.COLOR[1] * COLOR_MULTIPLIER, b: config.COLOR[2] * COLOR_MULTIPLIER };
      // let rgb = HSVtoRGB(a, 1.0, 1.0);
    }
    // data[stride + 2] = DecodeFloatRGBA([rgb.r, rgb.g, rgb.b]); //rgbToFloat([rgb.r, rgb.g, rgb.b], 255.);
    // data[stride + 3] = (a % 2) * Math.PI / 2;
    // data[stride + 3] = a % 2 * Math.PI; // Math.random() * Math.PI * 2;

    if (ANGLE_MODE === "group_anteradius") {
      data[stride + 3] = a * 2 * Math.PI + Math.PI; //randomizer.random() * Math.PI * 2; //a + Math.PI / 2; //Math.random() * Math.PI * 2;
    }
    else if (ANGLE_MODE === "group_radius") {
      data[stride + 3] = a * 2 * Math.PI;
    }
    else if (ANGLE_MODE === "grid") {
      if (gridGrp % 4 === 0) {
        data[stride + 3] = 3 * Math.PI / 2.;
      }
      else if (gridGrp % 4 === 3) {
        data[stride + 3] = Math.PI;
      }
      else if (gridGrp % 4 === 1) {
        data[stride + 3] = Math.PI / 2.0;
      }
      else {
        data[stride + 3] = 0.0;
      }
      // data[stride + 3] = (a % 2) * Math.PI / 2.;
    }
    else if (ANGLE_MODE === "gridsin") {
      let r = Math.sin(i / 100.) * 0.1;
      if (gridGrp % 4 === 0) {
        data[stride + 3] = 3 * Math.PI / 2. + r;
      }
      else if (gridGrp % 4 === 3) {
        data[stride + 3] = Math.PI + r;
      }
      else if (gridGrp % 4 === 1) {
        data[stride + 3] = Math.PI / 2.0 + r;
      }
      else {
        data[stride + 3] = 0.0 + r;
      }
      // data[stride + 3] = (a % 2) * Math.PI / 2.;
    }
    else if (ANGLE_MODE === "sin") {
      data[stride + 3] = Math.sin(i / 10.) * 0.1;
    }
    else if (ANGLE_MODE === "zero") {
      data[stride + 3] = 0.0;
    }
    else {
      data[stride + 3] = randomizer.random() * Math.PI * 2;
    }
    // data[stride + 3] = a;
    // velocity
    data[stride + 4] = SPEED;
    data[stride + 5] = SPEED;
    data[stride + 6] = 0.0;
    // data[stride + 7] = 0.0001 * (i - a * 100);
    if (TIME_MODE === "a") {
      data[stride + 7] = config.TIME_GROUP_INTERVAL * a; // 0.; //0.5 * (i % 100); //5 * a; //0.1 * (i - slot * 1000);
    }
    else if (TIME_MODE === "group") {
      data[stride + 7] = config.TIME_GROUP_INTERVAL * grp; // 0.; //0.5 * (i % 100); //5 * a; //0.1 * (i - slot * 1000);
    }
    else if (TIME_MODE === "group2") {
      data[stride + 7] = Math.sin(i * 100.0) * config.TIME_GROUP_INTERVAL * grp; // 0.; //0.5 * (i % 100); //5 * a; //0.1 * (i - slot * 1000);
    }
    else if (TIME_MODE === "i") {
      data[stride + 7] = config.TIME_GROUP_START + config.TIME_GROUP_INTERVAL * i;
    }
    else if (TIME_MODE === "groupi") {
      data[stride + 7] = config.TIME_GROUP_START + Math.floor(config.TIME_GROUP_INTERVAL * i / 3.0) * 3.0; //5 * a; //0.1 * (i - slot * 1000);
    }
    else {
      data[stride + 7] = 0.0;
    }

    colorData[colorStride] = rgb.r;
    colorData[colorStride + 1] = rgb.g;
    colorData[colorStride + 2] = rgb.b;
    colorData[colorStride + 3] = 1.0;

  }

  const texture = new THREE.DataTexture(
    data, PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT, THREE.RGBAFormat, THREE.FloatType,
    {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      generateMipmaps: false
    }
  );

  const colorTexture = new THREE.DataTexture(
    colorData, COLOR_DATA_WIDTH, COLOR_DATA_HEIGHT, THREE.RGBAFormat, THREE.FloatType,
    {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      generateMipmaps: false
    }
  );

  return [texture, colorTexture];
};

const createDataLocation = (width, height, config) => {
  if (!config) {
    return null;
  }
  const PARTICLE_COUNT = config.PARTICLE_COUNT;
  const PARTICLE_SIZE = config.PARTICLE_SIZE;
  const PARTICLE_COUNT_SQRT = Math.sqrt(PARTICLE_COUNT);
  const PARTICLE_DATA_WIDTH = PARTICLE_COUNT_SQRT * PARTICLE_DATA_SLOTS;
  const PARTICLE_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

  const COLOR_DATA_WIDTH = PARTICLE_COUNT_SQRT;
  const COLOR_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

  const step = 1.0 / PARTICLE_COUNT_SQRT;

  const vertices = new Float32Array(PARTICLE_COUNT * 3);
  for (let u, v, w, i = 0; i < PARTICLE_COUNT; i++) {
    u = i * 3;
    v = u + 1;
    w = u + 2;
    vertices[u] = (i % PARTICLE_COUNT_SQRT) * PARTICLE_DATA_SLOTS;
    vertices[v] = (i / PARTICLE_COUNT_SQRT);
    vertices[w] = 1.;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

  const uniforms = {
    physicsData: { value: null },
    trail: { value: null },
    colorData: { value: null },
    bounds: { value: new THREE.Vector2(PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT) },
    colorBounds: { value: new THREE.Vector2(COLOR_DATA_WIDTH, COLOR_DATA_HEIGHT) },
    screenBounds: { value: new THREE.Vector2(width, height) },
    particleSize: { value: PARTICLE_SIZE },
    step: { value: step },
    time: { value: 0 }
  };

  const material = new THREE.RawShaderMaterial({
    uniforms,
    vertexShader: render_vert, //glslify(require('./shaders/particles.vs')),
    fragmentShader: render_frag, // glslify(require('./shaders/particles.fs')),
    // depthTest: false,
    // transparent: true,
    // depthTest: true,
    // depthWrite: false,
    // side: THREE.DoubleSide,
    // alphaTest: 0.5,
    // blending: THREE.CustomBlending,
    // blendSrc: THREE.SrcAlphaFactor,
    // blendDst: THREE.OneMinusSrcAlphaFactor,
    // blending: THREE.AdditiveBlending
  });

  // const material = new THREE.PointsMaterial({ color: 0x888888 });
  const pointsScene = new THREE.Scene();
  const points = new THREE.Points(geometry, material);
  // points.frustumCulled = false;
  pointsScene.add(points);
  // const light = new THREE.AmbientLight(0xFFFFFF, 1.0);
  // pointsScene.add(light);
  return [points, pointsScene];
};

const createInitTrail = (width, height, config) => {
  const size = 4 * width * height;
  const data = new Float32Array(size);

  if ("INIT_TRAIL" in config) {
    if (config.INIT_TRAIL === "square") {
      let gridNb = 3;
      let gridWidth = 400;
      for (let g = 0; g < gridNb; g++) {
        let xStart = width / 2.0 + (g - Math.floor(gridNb / 2.0)) * width * 0.5 / gridNb - gridWidth / 2.0;
        let xEnd = xStart + gridWidth;
        let yStart = height / 2.0 + (g - Math.floor(gridNb / 2.0)) * width * 0.5 / gridNb - gridWidth / 2.0;
        let yEnd = yStart + gridWidth;

        for (let x = xStart; x < xEnd; x++) {
          for (let y = yStart; y < yEnd; y++) {
            let i = y * width * 4 + x * 4;
            data[i] = 100.0;
            data[i + 1] = 100.0;
            data[i + 2] = 100.0;
            data[i + 3] = 1.0;
          }
        }
      }

    }
    else if (config.INIT_TRAIL === "squares_hor") {
      let gridNb = config.RING_NB;
      let gridWidth = config.RING_WIDTH;
      for (let g = 0; g < gridNb; g++) {
        let xStart = width / 2.0 + (g - Math.floor(gridNb / 2.0)) * width * 0.5 / gridNb - gridWidth / 2.0;
        let xEnd = xStart + gridWidth;
        let yStart = height / 2.0 + - gridWidth / 2.0;
        let yEnd = yStart + gridWidth;

        for (let x = xStart; x < xEnd; x++) {
          for (let y = yStart; y < yEnd; y++) {
            let i = y * width * 4 + x * 4;
            data[i] = 100.0;
            data[i + 1] = 100.0;
            data[i + 2] = 100.0;
            data[i + 3] = 1.0;
          }
        }
      }

    }
    else if (config.INIT_TRAIL === "ring") {
      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          let i = y * width * 4 + x * 4;

          let X = x - width / 2.;
          let Y = y - height / 2.;
          let R2 = Math.pow(X, 2) + Math.pow(Y, 2)
          let D = Math.min(width, height);
          let RR2 = Math.pow(config.RADIUS_RATIO_X * D, 2);
          let RR21 = Math.pow(config.RADIUS_RATIO_Y * D + config.RING_WIDTH, 2);
          if (R2 > RR2 && R2 < RR21) {
            data[i] = 100.0;
            data[i + 1] = 100.0;
            data[i + 2] = 100.0;
            data[i + 3] = 1.0;
          }

        }
      }
    }

    else if (config.INIT_TRAIL === "multi-ring") {
      let ringNb = config.RING_NB;
      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          let i = y * width * 4 + x * 4;
          let X = x - width / 2.;
          let Y = y - height / 2.;
          let R2 = Math.pow(X, 2) + Math.pow(Y, 2)
          let D = Math.min(width, height);

          for (let n = 0; n < ringNb; n++) {
            let RR2 = Math.pow(n * config.RADIUS_RATIO_X * D / ringNb, 2);
            let RR21 = Math.pow(n * config.RADIUS_RATIO_Y * D / ringNb + config.RING_WIDTH, 2);

            if (R2 > RR2 && R2 < RR21) {
              data[i] = 100.0;
              data[i + 1] = 100.0;
              data[i + 2] = 100.0;
              data[i + 3] = 1.0;
            }
          }
        }
      }
    }
    else if (config.INIT_TRAIL === "grid") {
      let ringNb = config.RING_NB;
      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          let i = y * width * 4 + x * 4;
          let X = x - width / 2.;
          let Y = y - height / 2.;
          let D = Math.min(width, height);

          let initX = config.INIT_RATIO_X * D;
          let initY = config.INIT_RATIO_Y * D;

          for (let n = 0; n < ringNb; n++) {
            let xStart = initX + n * config.RADIUS_RATIO_X * D / ringNb;
            let xEnd = xStart + config.RING_WIDTH;
            let yStart = initY + n * config.RADIUS_RATIO_Y * D / ringNb;
            let yEnd = yStart + config.RING_WIDTH;

            if (
              (X >= xStart && X <= xEnd) ||
              (Y >= yStart && Y <= yEnd)
            ) {
              data[i] = 100.0;
              data[i + 1] = 100.0;
              data[i + 2] = 100.0;
              data[i + 3] = 1.0;
            }
          }
        }

      }

    }

    else if (config.INIT_TRAIL === "multi-ring-square") {
      let ringNb = config.RING_NB;
      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          let i = y * width * 4 + x * 4;
          let X = x - width / 2.;
          let Y = y - height / 2.;
          let D = Math.min(width, height);

          let initX = config.INIT_RATIO_X * D;
          let initY = config.INIT_RATIO_Y * D;

          for (let n = 0; n < ringNb; n++) {
            let xStart = initX + n * config.RADIUS_RATIO_X * D / ringNb;
            let xEnd = xStart + config.RING_WIDTH;
            let yStart = initY + n * config.RADIUS_RATIO_Y * D / ringNb;
            let yEnd = yStart + config.RING_WIDTH;

            if (
              ((X >= xStart && X <= xEnd) &&
                (Y >= -yEnd && Y <= yEnd)) ||
              ((X <= -xStart && X >= -xEnd) &&
                (Y >= -yEnd && Y <= yEnd)) ||
              ((Y >= yStart && Y <= yEnd) &&
                (X >= -xStart && X <= xEnd)) ||
              ((Y <= -yStart && Y >= -yEnd) &&
                (X >= -xStart && X <= xEnd))
            ) {
              data[i] = 100.0;
              data[i + 1] = 100.0;
              data[i + 2] = 100.0;
              data[i + 3] = 1.0;
            }
          }
        }

      }

    }


  }

  const texture = new THREE.DataTexture(
    data, width, height, THREE.RGBAFormat, THREE.FloatType,
    {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      generateMipmaps: false
    }
  );

  return texture;
}


// Required style metadata
const styleMetadata = {
  name: '',
  description: '',
  image: '',
  creator_name: '',
  options: {
    mod1: 0.4,
    mod2: 0.1,
    mod3: 0.4,
    color1: '#fff000',
    background: '#000000',
  },
};
function buf2hex(buffer) { // buffer is an ArrayBuffer
  return [...new Uint8Array(buffer)]
    .map(x => x.toString(16).padStart(2, '0'))
    .join('');
}

function hex2buf(hex) {
  var buffer = new ArrayBuffer(hex.length / 2);
  var array = new Uint8Array(buffer);
  var k = 0;
  for (var i = 0; i < hex.length; i += 2) {
    array[k] = parseInt(hex[i] + hex[i + 1], 16);
    k++;
  }

  return buffer;
}

function log(...txt) {
  if (process.env.NODE_ENV != "production") {
    console.log(...txt);
  }
}

const ec = new TextEncoder();
const dec = new TextDecoder();
const beta = new Uint8Array([
  249, 67, 136, 194, 98,
  66, 14, 207, 222, 0,
  153, 116, 167, 180, 118,
  238
]);

const alpha = new Uint8Array([
  145, 23, 158, 92, 89, 248, 155, 111,
  147, 122, 60, 241, 144, 10, 2, 22,
  64, 159, 32, 214, 15, 158, 98, 246,
  54, 31, 109, 33, 163, 16, 235, 29
]);

const ENV = process.env.NODE_ENV;

export { styleMetadata };


function Inner({
  block,
  attributesRef,
  canvasRef,
  errorFun,
  precisionFun,
  recordingFun,
  initFun,
  collection, tokenId,

  mod1 = 0.75, // Example: replace any number in the code with mod1, mod2, or color values
  mod2 = 0.25,
  mod3 = 0.4,
  color1 = '#4f83f1',
  background = '#ccc',
}) {
  log(`Rendering`);

  const physicsQuad = useRef();
  const physicsScene = useRef();

  const finalQuad = useRef();
  const [WIDTH, setWIDTH] = useState(1920);
  const [HEIGHT, setHEIGHT] = useState(1080);

  // Three
  const { gl, size, camera, scene } = useThree();
  const { width, height } = size;


  const updateCamera = (width, height, WIDTH, HEIGHT) => {
    let ratio = width / height;
    let trailRatio = WIDTH / HEIGHT;
    let rh = height / HEIGHT;
    let rw = width / WIDTH;
    let r = ratio / trailRatio; // (width / height) / (WIDTH / HEIGHT) => (width / WIDTH) * (HEIGHT / height) => rw / rh

    if (width <= height) {
      if (ratio <= trailRatio) {
        camera.left = HEIGHT / -2;
        camera.right = HEIGHT / 2;
        camera.top = HEIGHT / 2;
        camera.bottom = HEIGHT / -2;
        gl.setSize(width, width);
      }
      else {
        // camera.left = width * r / -2;
        // camera.right = width * r / 2;
        // camera.top = height * r / 2;
        // camera.bottom = height * r / -2;
        camera.left = WIDTH / -2;
        camera.right = WIDTH / 2;
        camera.top = HEIGHT / 2;
        camera.bottom = HEIGHT / -2;

        gl.setSize(width * r, height * r);
      }
    }

    else if (width <= HEIGHT) {
      if (ratio <= trailRatio) {
        camera.left = HEIGHT * ratio / -2;
        camera.right = HEIGHT * ratio / 2;
        camera.top = HEIGHT / 2;
        camera.bottom = HEIGHT / -2;
        gl.setSize(width, height);
      }
      else {
        // camera.left = HEIGHT * r / -2;
        // camera.right = HEIGHT * r / 2;
        // camera.top = height * r * HEIGHT / width / 2;
        // camera.bottom = height * r * HEIGHT / width / -2;
        camera.left = HEIGHT * ratio / -2;
        camera.right = HEIGHT * ratio / 2;
        camera.top = HEIGHT / 2;
        camera.bottom = HEIGHT / -2;

        gl.setSize(width, height);
      }

    }


    else if (width <= WIDTH) {
      // if (ratio > trailRatio) {
      //   camera.left = WIDTH / -2;
      //   camera.right = WIDTH / 2;
      //   camera.top = HEIGHT / 2;
      //   camera.bottom = HEIGHT / -2;

      //   gl.setSize(width * rw, height / rh);
      // }
      // else {
      camera.left = HEIGHT * ratio / -2;
      camera.right = HEIGHT * ratio / 2;
      camera.top = HEIGHT / 2;
      camera.bottom = HEIGHT / -2;

      gl.setSize(width, height);
      // }
    }
    else {
      camera.left = WIDTH / -2;
      camera.right = WIDTH / 2;
      camera.top = HEIGHT / 2;
      camera.bottom = HEIGHT / -2;

      gl.setSize(width * r, height * r);
    }
    camera.updateProjectionMatrix();
  }
  // Handle correct scaling of scene as canvas is resized, and when generating upscaled version.
  useEffect(() => {
    log(`updating camera...`);
    gl.setPixelRatio(window.devicePixelRatio);
    // let DEFAULT_SIZE = 500;
    // let DIM = Math.min(width, height);
    // let M = DIM / DEFAULT_SIZE;
    camera.zoom = 1.0;
    // camera.zoom = 100;
    // camera.left = WIDTH / -2;
    // camera.right = WIDTH / 2;
    // camera.top = HEIGHT / 2;
    // camera.bottom = HEIGHT / -2;
    // updateCamera(width, height, WIDTH, HEIGHT);
    if (configData && stopAnim.current) {
      updateCamera(width, height, configData.WIDTH, configData.HEIGHT);
    }
    // camera.aspect = WIDTH / HEIGHT;


    // let ctx = gl.getContext();
    // ctx.disable(ctx.DITHER);

    // let p = gl.getContext().getShaderPrecisionFormat(gl.getContext().VERTEX_SHADER, gl.getContext().HIGH_FLOAT);
    // let p2 = gl.getContext().getShaderPrecisionFormat(gl.getContext().FRAGMENT_SHADER, gl.getContext().HIGH_FLOAT);
    // precisionFun(p2.rangeMin + " " + p2.rangeMax + " " + p2.precision);
    // precisionFun("precision:" + gl.precision + " depth:" + gl.depth + " powerPreference:" + gl.powerPreference + " stencil:" + gl.stencil);
    precisionFun("ratio:" + window.devicePixelRatio + " " + gl.getPixelRatio());
    // gl.setSize(WIDTH, HEIGHT);
    // gl.setViewport(0, 0, width, height);
  }, [camera, width, height]);

  const [configData, setConfigData] = useState(null);
  const [physicsData, setPhysicsData] = useState(null); //createPhysicsDataTexture(WIDTH, HEIGHT, data));
  const [colorData, setColorData] = useState(null);
  const [dataLocation, setDataLocation] = useState(null); //createDataLocation(WIDTH, HEIGHT, data));

  // useEffect(() => {
  //   setConfigData(data);
  //   setPhysicsData(createPhysicsDataTexture(WIDTH, HEIGHT, data));
  //   setDataLocation(createDataLocation(WIDTH, HEIGHT, data));
  // }, [tokenId]);
  const textureA = useRef();
  const textureB = useRef();
  const writeBuffer = useRef();
  const readBuffer = useRef();
  const trail = useRef();
  const inputData = useRef();
  const inputTrailData = useRef();

  const diffuseQuad = useRef();
  const diffuseScene = useRef();
  const decayQuad = useRef();
  const decayScene = useRef();

  useEffect(() => {
    // gettkc(alpha, beta).then((a) => {
    //   let [c, v] = a
    //   if (c == null || v == null) {
    //     errorFun(true);
    //   }
    //   collection.current = c;
    //   tokenId.current = v;
    //   log("collection", collection.current, "tokenId", tokenId.current);
    //   const u = "./tokens/" + collection.current + "/" + tokenId.current + ".json";
    //   log("Loading Config from", u);
    //   return fetch(u);
    // })
    log("collection", collection, "tokenId", tokenId);
    let prom;
    if (collection && tokenId) {
      const u = "./tokens/" + collection + "/" + tokenId + ".json";
      log("Loading Config from", u);
      prom = fetch(u).then((res) => {
        return res.json();
      })
    } else {
      prom = Promise.resolve(STATIC_CONFIG);
    }
    prom.then((data) => {
      // store Data in State Data Variable
      log("configData", data);

      let main = document.getElementById('main');
      main.title = data.NAME + " - " + main.title;

      const WIDTH = data.WIDTH;
      setWIDTH(data.WIDTH);
      const HEIGHT = data.HEIGHT;
      setHEIGHT(data.HEIGHT);

      setConfigData(data);
      const [pd, cd] = createPhysicsDataTexture(WIDTH, HEIGHT, data);
      setPhysicsData(pd);
      setColorData(cd);
      const dl = createDataLocation(WIDTH, HEIGHT, data);
      setDataLocation(dl);

      const td = createInitTrail(WIDTH, HEIGHT, data);
      inputTrailData.current = td;

      log("Loaded Config");

      log("Building Buffers");
      const PARTICLE_COUNT_SQRT = Math.sqrt(data.PARTICLE_COUNT);
      const PARTICLE_DATA_WIDTH = PARTICLE_COUNT_SQRT * PARTICLE_DATA_SLOTS;
      const PARTICLE_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

      textureA.current = new THREE.WebGLRenderTarget(PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT, {
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false,
        format: THREE.RGBAFormat, type: THREE.FloatType, stencilBuffer: false, antiAlias: false,
        // wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping
      });
      writeBuffer.current = new THREE.WebGLRenderTarget(WIDTH, HEIGHT, {
        minFilter: THREE.LinearFilter, magFilter: THREE.NearestFilter, generateMipmaps: false,
        format: THREE.RGBAFormat, type: THREE.FloatType, stencilBuffer: false, antiAlias: false,
        // wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping
      });

      if (readBuffer.current) {
        // rbf = readBuffer.current;
        // trl = trail.current;
        inputData.current = textureB.current.texture;
        textureB.current = textureB.current;
      }
      else {
        textureB.current = new THREE.WebGLRenderTarget(PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT, {
          minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false,
          format: THREE.RGBAFormat, type: THREE.FloatType, stencilBuffer: false, antiAlias: false,
          // wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping
        });

        readBuffer.current = writeBuffer.current.clone();
        trail.current = writeBuffer.current.clone();
        inputData.current = pd;
      }

      log("Building Physics");
      let PARTICLE_RANDOM = 1;
      if ("PARTICLE_RANDOM" in data) {
        PARTICLE_RANDOM = data.PARTICLE_RANDOM
      }

      let REBOUND = 1;
      if ("REBOUND" in data) {
        REBOUND = data.REBOUND
      }

      let SPEED_DECREASE = 1.0;
      if ("SPEED_DECREASE" in data) {
        SPEED_DECREASE = data.SPEED_DECREASE
      }

      let FINAL_COLOR_MULTIPLIER = 1.0;
      if ("FINAL_COLOR_MULTIPLIER" in data) {
        FINAL_COLOR_MULTIPLIER = data.FINAL_COLOR_MULTIPLIER;
      }

      let PALETTE_TYPE = 0.0;
      let PALETTE_A = [0.0, 0.0, 0.0];
      let PALETTE_B = [0.0, 0.0, 0.0];
      let PALETTE_C = [0.0, 0.0, 0.0];
      let PALETTE_D = [0.0, 0.0, 0.0];

      if ("PALETTE_TYPE" in data) {
        PALETTE_TYPE = data.PALETTE_TYPE;
        if (PALETTE_TYPE === 1) {
          PALETTE_A = data.PALETTE_A;
          PALETTE_B = data.PALETTE_B;
          PALETTE_C = data.PALETTE_C;
          PALETTE_D = data.PALETTE_D;
        }
      }

      const uniforms = {
        physicsData: { value: inputData.current },
        trail: { value: readBuffer.current.texture },
        backTrail: { value: inputTrailData.current },
        bounds: { value: new THREE.Vector2(PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT) },
        screenBounds: { value: new THREE.Vector2(WIDTH, HEIGHT) },
        time: { value: 0.0 },
        sensorLength: { value: data.SENSOR_LENGTH },
        sensorAngle: { value: data.SENSOR_ANGLE },
        particleAngle: { value: data.PARTICLE_ANGLE },
        useRandom: { value: PARTICLE_RANDOM },
        speedDecrease: { value: SPEED_DECREASE },
        rebound: { value: REBOUND }
      };

      const material = new THREE.RawShaderMaterial({
        uniforms,
        vertexShader: physics_vertex,
        fragmentShader: physics_frag,
        // depthTest: false,
        // transparent: true,
        // transparent: false,
        // blending: THREE.AdditiveBlending
      });

      const quadScene = new THREE.Scene();
      const quadPlane = new THREE.PlaneBufferGeometry(PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT);
      const quad = new THREE.Mesh(quadPlane, material);
      quadScene.add(quad);

      physicsQuad.current = quad;
      physicsScene.current = quadScene;

      log("Building Diffuse");
      const diffuseQuadPlane = new THREE.PlaneBufferGeometry(WIDTH, HEIGHT);

      const diffuseMaterial = new THREE.RawShaderMaterial({
        uniforms: DiffuseShader.uniforms,
        vertexShader: DiffuseShader.vertexShader,
        fragmentShader: DiffuseShader.fragmentShader,
        // depthTest: false,
        // transparent: true,
        // transparent: false,
        // blending: THREE.AdditiveBlending
      });
      diffuseMaterial.uniforms.bounds.value = new THREE.Vector2(WIDTH, HEIGHT);
      diffuseMaterial.uniforms.ratio.value = data.DIFFUSE_RATIO;
      diffuseMaterial.uniforms.trailRatio.value = data.DIFFUSE_TRAIL_RATIO;
      diffuseMaterial.uniforms.trailSmooth.value = data.DIFFUSE_TRAIL_SMOOTH;
      // const material = new THREE.MeshBasicMaterial();
      diffuseQuad.current = new THREE.Mesh(diffuseQuadPlane, diffuseMaterial);
      // const light = new THREE.AmbientLight(0xFFFFFF, 1.0);
      diffuseScene.current = new THREE.Scene();
      diffuseScene.current.add(diffuseQuad.current);

      log("Building Decay");
      const decayQuadPlane = new THREE.PlaneBufferGeometry(WIDTH, HEIGHT);
      const decayMaterial = new THREE.RawShaderMaterial({
        uniforms: DecayShader.uniforms,
        vertexShader: DecayShader.vertexShader,
        fragmentShader: DecayShader.fragmentShader,
        // depthTest: false,
        // transparent: true,
        // transparent: false,
        // blending: THREE.AdditiveBlending
      });
      decayMaterial.uniforms.decay.value = data.DECAY;
      // const material = new THREE.MeshBasicMaterial();
      decayQuad.current = new THREE.Mesh(decayQuadPlane, decayMaterial);
      // const light = new THREE.AmbientLight(0xFFFFFF, 1.0);
      decayScene.current = new THREE.Scene();
      decayScene.current.add(decayQuad.current);

      log("Building Final");
      const finalQuadPlane = new THREE.PlaneBufferGeometry(WIDTH, HEIGHT);

      const finalMaterial = new THREE.RawShaderMaterial({
        uniforms: ResizeShader.uniforms,
        vertexShader: ResizeShader.vertexShader,
        fragmentShader: ResizeShader.fragmentShader,
        // depthTest: false,
        // transparent: true,
        // transparent: false,
        // blending: THREE.AdditiveBlending
      });
      finalMaterial.uniforms.bounds.value = new THREE.Vector2(WIDTH, HEIGHT);
      finalMaterial.uniforms.screenBounds.value = new THREE.Vector2(WIDTH, HEIGHT);
      finalMaterial.uniforms.colorMultiplier.value = FINAL_COLOR_MULTIPLIER;
      finalMaterial.uniforms.paletteType.value = PALETTE_TYPE;
      finalMaterial.uniforms.paletteA.value = PALETTE_A;
      finalMaterial.uniforms.paletteB.value = PALETTE_B;
      finalMaterial.uniforms.paletteC.value = PALETTE_C;
      finalMaterial.uniforms.paletteD.value = PALETTE_D;

      // const material = new THREE.MeshBasicMaterial();
      finalQuad.current = new THREE.Mesh(finalQuadPlane, finalMaterial);
      // quad.scale.x = width / WIDTH;
      // quad.scale.y = height / HEIGHT;
      // const light = new THREE.AmbientLight(0xFFFFFF, 1.0);
      scene.add(finalQuad.current);

      if (recordingFun) {
        recordingFun(gl.domElement);
      }

      initFun(true);
    }).catch((err) => {
      log("Error", err);
      errorFun(true);
    })
  }, [tokenId]);


  function swapBuffers(b1, b2) {
    let t = b2.current;
    b2.current = b1.current;
    b1.current = t;
  }

  const stopAnim = useRef();

  stopAnim.current = useMemo(() => {
    if (stopAnim.current) {
      return stopAnim.current;
    }
    else {
      return false;
    }
  });

  const onClick = (event) => {
    stopAnim.current = !stopAnim.current;
  }

  const onDocumentKeyDown = (event) => {
    let keyCode = event.which;
    if (keyCode == 32) {
      stopAnim.current = !stopAnim.current;
    }
  }

  useEffect(() => {
    // init three.interaction on mount
    gl.domElement.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onDocumentKeyDown, false);
  }, [])


  const [timeElapsed, setTimeElapsed] = useState(0.0);
  // timeElapsed.current = 0.0;

  return useFrame((state, delta) => {
    setTimeElapsed(timeElapsed + delta);
    if (stopAnim.current || !configData || !finalQuad.current
    ) {
      return;
    }

    const WIDTH = configData.WIDTH;
    const HEIGHT = configData.HEIGHT;

    // gl.autoClearColor = true;
    camera.left = WIDTH / -2;
    camera.right = WIDTH / 2;
    camera.top = HEIGHT / 2;
    camera.bottom = HEIGHT / -2;
    camera.updateProjectionMatrix();

    const PARTICLE_COUNT_SQRT = Math.sqrt(configData.PARTICLE_COUNT);
    const PARTICLE_DATA_WIDTH = PARTICLE_COUNT_SQRT * PARTICLE_DATA_SLOTS;
    const PARTICLE_DATA_HEIGHT = PARTICLE_COUNT_SQRT;
    const COLOR_DATA_WIDTH = PARTICLE_COUNT_SQRT;
    const COLOR_DATA_HEIGHT = PARTICLE_COUNT_SQRT;

    // PHYSICS
    gl.setSize(PARTICLE_DATA_WIDTH, PARTICLE_DATA_HEIGHT, false);
    gl.setRenderTarget(textureA.current);
    physicsQuad.current.material.uniforms.time.value = timeElapsed;
    gl.render(physicsScene.current, camera);

    // Render particles
    gl.setSize(WIDTH, HEIGHT);
    gl.setRenderTarget(writeBuffer.current);
    dataLocation[0].material.uniforms.physicsData.value = textureA.current.texture;
    dataLocation[0].material.uniforms.trail.value = trail.current.texture;
    dataLocation[0].material.uniforms.colorData.value = colorData;
    dataLocation[0].material.uniforms.time.value = timeElapsed;

    gl.render(dataLocation[1], camera);
    swapBuffers(readBuffer, writeBuffer);

    // Diffuse particles on trail
    gl.setRenderTarget(writeBuffer.current);
    diffuseQuad.current.material.uniforms.tDiffuse.value = readBuffer.current.texture;
    diffuseQuad.current.material.uniforms.trail.value = trail.current.texture;
    diffuseQuad.current.material.uniforms.time.value = timeElapsed;
    gl.render(diffuseScene.current, camera);
    swapBuffers(readBuffer, writeBuffer);

    // Decay particles on trail
    gl.setRenderTarget(writeBuffer.current);
    decayQuad.current.material.uniforms.tDiffuse.value = readBuffer.current.texture;
    gl.render(decayScene.current, camera);
    swapBuffers(readBuffer, writeBuffer);

    updateCamera(width, height, WIDTH, HEIGHT);

    gl.setRenderTarget(null);
    // let texture = readBuffer.current.texture;
    // log("texture", texture);
    // texture.wrapS = THREE.RepeatWrapping;
    // texture.wrapT = THREE.RepeatWrapping;
    // texture.repeat.set(0.5, 1.0);
    // texture.offset.set(0, 0);
    // texture.repeat.set(2, 2);
    // texture.offset.set(+(WIDTH - width) / 2.0, +(HEIGHT - height) / 2.0); // = new THREE.Vector2(-id, 0);
    // texture.needsUpdate = true;

    finalQuad.current.material.uniforms.tDiffuse.value = readBuffer.current.texture;
    // finalQuad.current.scale.x = width / WIDTH;
    // finalQuad.current.material.map = readBuffer.current.texture;
    // finalQuad.current.material.needsUpdate = true;
    // finalQuad.current.material.uniforms.texture.value.needsUpdate = true;
    // camera.position.z = 10;
    gl.render(scene, camera);

    swapBuffers(textureA, textureB);
    swapBuffers(trail, readBuffer);

    physicsQuad.current.material.uniforms.physicsData.value = textureB.current.texture;

    // scene.background = new THREE.Color(background, 0.0);
    // physicsQuad.current.material.needsUpdate = true;
  });

  // Render the scene
  // return (
  //   <group ref={group} position={[-0, 0, 0]} rotation={[0, 0, 0]}>
  //     <ambientLight intensity={1} color={color} />
  //     <primitive object={obj.current} />
  //   </group>
  // );
  // return null; 
}
function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function mobileCheck() {
  let check = false;
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

const Outer = React.memo(({ gl, background, width, height, ...props }) => {
  const [hasError, setHasError] = useState(true);
  const [precision, setPrecision] = useState("Unknown");
  const recorderRef = useRef();
  const downloadLink = useRef();

  useEffect(() => {
    if (gl.current && gl.current.setClearColor && background) {
      gl.current.setClearColor(background);
      // gl.current.autoClearColor = true;
    }
    log("Recreating");
  }, [background, gl]);

  const [c, setC] = useState(null);
  const [isInit, setInit] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [tokenId, setTokenId] = useState(null);
  const [collection, setCollection] = useState(null);
  const [vidUrl, setVidUrl] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [isMobile, setMobile] = useState(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  const [isIframe, setIFrame] = useState(inIframe());
  const [isFull, setFull] = useState(false);
  const [fullLink, setFullLink] = useState("");
  const [recorder, setRecorder] = useState(null);

  // [c.current, tokenId.current, collection.current] =
  useEffect(() => {
    const url = new URL(document.URL);
    const c = url.searchParams.get("c");
    const full = url.searchParams.get("full");
    const ml = url.searchParams.get("mobile");
    const tokenId = url.searchParams.get("tokenId");
    const collection = url.searchParams.get("collection");
    const recorderParam = url.searchParams.get("recorder");
    if (ml) {
      setMobile(true);
    }

    if (!c && !tokenId && !collection && STATIC_CONFIG) {
      setTokenId(null);
      setCollection(null);
      let v = './media/' + STATIC_CONFIG.COLLECTION + "/" + STATIC_CONFIG.TOKEN_ID + ".mp4";
      let img = './media/' + STATIC_CONFIG.COLLECTION + "/" + STATIC_CONFIG.TOKEN_ID + ".png";
      setVidUrl(v);
      setImgUrl(img);
      // log("collection", collection, "tokenId", tokenId, "vidUrl", v);
      setHasError(false);
      log("Params Initialized");
    }
    else {
      if (!full) {
        const fl = url.toString() + "&full=1";
        setFullLink(fl);
        setFull(false);
      } else {
        setFullLink(url.toString());
        setFull(true);
      }
      if (ENV == "production") {
        if (c == null) {
          setHasError(true);
        }
        setC(c);
      }
      else {
        setTokenId(tokenId);
        setCollection(collection);
      }

      if (c) {
        gettkc(alpha, beta, c).then((a) => {
          if (a == null) {
            setHasError(true);
          } else {
            let [col, tok] = a;
            if (col == null || tok == null) {
              setHasError(true);
            }
            setCollection(col);
            setTokenId(tok);
            let v = './media/' + col + "/" + tok + ".mp4";
            let img = './media/' + col + "/" + tok + ".png";
            setVidUrl(v);
            setImgUrl(img);
            // log("collection", col, "tokenId", tok, "vidUrl", v);
          }
        })
        setHasError(false);
        log("Params Initialized");
      } else if (ENV != "production" && collection && tokenId) {
        let v = './media/' + collection + "/" + tokenId + ".mp4";
        let img = './media/' + collection + "/" + tokenId + ".png";
        setVidUrl(v);
        setImgUrl(img);
        // log("collection", collection, "tokenId", tokenId, "vidUrl", v);
        setHasError(false);
        log("Params Initialized");
      } else {
        setHasError(true);
      }
    }
    if (recorderParam && ENV != "production") {
      setRecorder(new CanvasRecorder());
    }
    setInit(true);
  }, [background, gl]);


  async function gettkc(key, iv, c) {
    if (ENV == "production" || c) {
      return window.crypto.subtle.importKey("raw", key, {
        name: 'AES-CBC',
        length: 256
      }, true, ["encrypt", "decrypt"]).then((importedKey) => {
        // txt = txt.padEnd(16, " ");
        return window.crypto.subtle.decrypt(
          {
            name: "AES-CBC",
            iv: iv
          },
          importedKey,
          hex2buf(c)
        );
      }).then((decrypted) => {
        const dc = dec.decode(decrypted);
        let [col, tok] = dc.split("_");
        return [col, tok];
      }).catch((err) => {
        log("Error", err);
        setHasError(true);
      });
    } else {
      return Promise.resolve([collection, tokenId]);
    }
  }

  const startRecording = useCallback((cnv) => {
    if (recorder) {
      cnv = document.querySelector('canvas');
      console.log("Start Recording");
      recorder.createStream(cnv);
      recorder.start();
    }
  }, [recorder]);

  const stopRecording = useCallback(() => {
    console.log("rec2", recorder)
    if (recorder) {
      recorder.stop();
      const file = recorder.save();
      console.log("file", file);
      // Do something with the file
      downloadLink.current.href = URL.createObjectURL(file);
      downloadLink.current.download = 'test.webm';
    }
  }, [recorder]);

  let playerHeight;
  if (isIframe) {
    playerHeight = height;
  } else {
    playerHeight = height - 20;
  }

  return (<div style={{
    margin: '0 auto',
    width: '100vw',
    height: '100vh'
  }}>
    {(!isInit || (!isLoaded && !isMobile)) && (<>
      <div style={{
        display: "flex",
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>Loading... Plz reload if taking too long...</div>
    </>)}
    {isInit && hasError && <ErrorComponent></ErrorComponent>}
    {/* <div>PRECISION: {precision}</div> */}
    {recorder &&
      (<div><button onClick={startRecording}>Start Recording</button>
        <button onClick={stopRecording}>Stop Recording</button>
        <a ref={downloadLink}>Download</a></div>)
    }

    {(isInit && !hasError && (!isFull && isMobile)) && (
      <div style={{
        display: "flex",
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }} width={Math.min(width, height)} height={Math.min(width, height)}>
        <div style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <ReactPlayer url={vidUrl} playing={true} muted={true} loop={true} controls={true} width={Math.min(width, playerHeight)} height={Math.min(width, playerHeight)} />
        </div>

        {!isIframe && <a href={fullLink} style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center'
        }}>Video in limited device. Click to try full sim. Can render ugly.</a>
        }
      </div>
    )}
    {(isInit && !hasError && (isFull || !isMobile)) &&
      (<>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{"Naidbian - " + collection + " Collection"}</title>
          <meta property={"og:image"} content={imgUrl} />
        </Helmet>
        <Canvas
          // ref={recorderRef}
          key="canvas"
          // invalidateFrameloop
          colorManagement
          orthographic
          camera={{ zoom: 1, position: [0, 0, 100] }}
          // camera={camera}
          gl={{
            preserveDrawingBuffer: true,
            // premultipliedAlpha: true,
            antialias: false,
            // stencil: false,
            // generateMipmaps: false,
            // wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping,
            // powerPreference: "high-performance",
            // depth: false,
            // stencil: true
          }}
          onCreated={(context) => {
            // canvasRef.current = context.gl.domElement;
            gl.current = context.gl;
            gl.current.setClearColor(background);

          }}
          pixelRatio={window.devicePixelRatio}
          style={{
            display: "flex",
            alignItems: "center"
          }}
        // sx={{
        //   width: '100%',
        //   height: '100%',
        // }}
        >
          {/* <ambientLight intensity={1} color={0xffffff} /> */}
          < Inner {...props} canvasRef={gl} background={background}
            errorFun={setHasError} precisionFun={setPrecision}
            recordingFun={startRecording}
            initFun={setLoaded}
            tokenId={tokenId} collection={collection} />
        </Canvas>
      </>
      )
    }

  </div >);
});

function ErrorComponent() {
  return <h2>Invalid Url called by your NFT, please contact @naibdian on twitter to solve your issue!</h2>
}

export default Outer;


/* accepts parameters
 * h  Object = {h:x, s:y, v:z}
 * OR 
 * h, s, v
*/
function HSVtoRGB(h, s, v) {
  let r, g, b, i, f, p, q, t;
  if (arguments.length === 1) {
    s = h.s, v = h.v, h = h.h;
  }
  i = Math.floor(h * 6);
  f = h * 6 - i;
  p = v * (1 - s);
  q = v * (1 - f * s);
  t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: r = v, g = t, b = p; break;
    case 1: r = q, g = v, b = p; break;
    case 2: r = p, g = v, b = t; break;
    case 3: r = p, g = q, b = v; break;
    case 4: r = t, g = p, b = v; break;
    case 5: r = v, g = p, b = q; break;
  }
  return {
    // r: Math.round(r * 255),
    // g: Math.round(g * 255),
    // b: Math.round(b * 255)
    r: r, g: g, b: b
  };
}


class CanvasRecorder {

  constructor() {
    log("Building CanvasRecorder");
    this.recordedBlobs = [];
    this.stream = null;
    this.supportedType = null;
    this.mediaRecorder = null;
  }

  createStream(canvas) {
    this.stream = canvas.captureStream(150)
  }

  // captureMediaStream(mediaStream) {
  //   this.stream = mediaStream
  // }

  // recordScreen = async () => {
  //   stream = await navigator.mediaDevices.getDisplayMedia({
  //     video: { mediaSource: 'screen' }
  //   })
  // }

  start() {
    let types = [
      'video/webm',
      'video/webm,codecs=vp9',
      'video/vp8',
      'video/webm;codecs=vp8',
      'video/webm;codecs=daala',
      'video/webm;codecs=h264',
      'video/mpeg'
    ]
    for (let i in types) {
      if (MediaRecorder.isTypeSupported(types[i])) {
        this.supportedType = types[i]
        break
      }
    }
    if (this.supportedType == null) {
      console.log('No supported type found for MediaRecorder')
    }
    let options = {
      // mimeType: this.supportedType,
      // videoBitsPerSecond: 25000000000 // 2.5Mbps
      videoBitsPerSecond: 2048000,
      mimeType: 'video/webm;codecs=h264'
    }
    this.recordedBlobs = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, options)
      // this.mediaRecorder = new MediaRecorder(this.stream)
    } catch (e) {
      console.error('Exception while creating MediaRecorder:', e)
      alert('MediaRecorder is not supported by this browser.')
      return
    }
    const self = this;
    this.mediaRecorder.onstop = event => { self.handleStop(event) };
    this.mediaRecorder.ondataavailable = event => { self.handleDataAvailable(event) };
    this.mediaRecorder.start(100) // collect 100ms of data blobs
  }

  handleDataAvailable(event) {
    if (event.data && event.data.size > 0) {
      this.recordedBlobs.push(event.data)
    }
  }
  handleStop(event) {
    const superBuffer = new Blob(this.recordedBlobs, { type: this.supportedType })
  }

  stop() {
    this.mediaRecorder.stop()
  }

  save(file_name) {
    return new Blob(this.recordedBlobs, { type: this.supportedType })
  }

}